/**
 * The open task lives in the URL as `?task=<id>`, so a task can be linked to
 * and the browser back button closes the drawer (on a phone the drawer is a
 * full-screen sheet, and back is how people expect to leave it).
 *
 * Only OPENING the drawer adds a history entry. Moving between tasks while it
 * is open — parent, sibling, child, another row — replaces that entry, so
 * walking the tree never leaves a trail of steps to back out through.
 */
import { onBeforeUnmount, watch } from "vue";
import { PERSONAL, useBoard } from "../stores/board.js";
import { quickTaskIn } from "./quickTask.js";
import { repoInfos, repoOnBoard } from "./repoSettings.js";
import { hasDrawer, useViewport, VIEWS, type View } from "../stores/viewport.js";

const PARAM = "task";

/** Marks the entry we pushed, so closing knows it can step back over it. A
 *  deep link has no such entry behind it, and back would leave the app.
 *  The Android app pushes this same marker, and the `task` param, to open a
 *  drawer from its widget without a reload (android/…/MainActivity.kt,
 *  openTask) — rename either and change it there too. */
const PUSHED = "todosPushed";

function urlFor(id: string | null): string {
  const url = new URL(location.href);
  if (id) url.searchParams.set(PARAM, id);
  else url.searchParams.delete(PARAM);
  return url.pathname + url.search + url.hash;
}

export function taskLink(id: string): string {
  return location.origin + urlFor(id);
}

/** A task's drawer as a page of its own, for a link's `href` on a view with no
 *  drawer: what a new tab or a copied link opens. A plain click does not follow
 *  it — see `openTaskFromView`. */
export function taskHref(id: string): string {
  const url = new URL(location.href);
  url.searchParams.set(PARAM, id);
  url.searchParams.set("view", "board");
  url.searchParams.delete("session");
  url.searchParams.delete("queued");
  return url.pathname + url.search + url.hash;
}

/**
 * Open a task's drawer from a view that has none (the tmux view's header), so
 * that back comes BACK to that view. Selecting first pushes the drawer's entry
 * while the address still names the view being left; only then is the view
 * switched, which replaces the new entry, not that one. Back then lands on an
 * entry naming a view with no drawer, and `selectFromUrl` follows it there.
 */
export function openTaskFromView(id: string): void {
  const board = useBoard();
  if (!board.tasks.has(id)) return;
  commitFocusedField();
  board.select(id);
  useViewport().view = "board";
}

/**
 * Open a task in the modal the tmux view draws (TaskModal.vue), staying on
 * that view. The selection and its history entry are the drawer's own, so back
 * closes it, as it closes the drawer.
 */
export function openTaskHere(id: string): void {
  const board = useBoard();
  if (!board.tasks.has(id)) return;
  commitFocusedField();
  board.select(id);
}

/** Close the open task, saving the field being typed in first — the modal is
 *  closed by a key or a press outside it, neither of which blurs the field. */
export function closeTask(): void {
  commitFocusedField();
  useBoard().select(null);
}

/**
 * The drawer saves each field on `change`, which the browser fires only when
 * the field loses focus. Clicking anything in the page does that first; the
 * back button (or Alt+←, or a mouse's back key) does not, so the drawer would
 * switch or unmount with the edit still sitting in the input.
 *
 * `blur()` is not enough to force it: a page without system focus (another
 * window in front, a background tab) gets no focus events at all, so no
 * `change` either. Dispatching `change` ourselves always runs the handler, and
 * it runs while the drawer still shows the task being edited, so the save goes
 * to the right id. The handlers compare against the stored value, so a field
 * that was not actually edited writes nothing.
 *
 * Text-like fields only: a range or checkbox commits the moment it moves, and
 * re-sending a slider's value would pin progress that was following status.
 */
const COMMIT_ON_CHANGE = new Set(["text", "number", "search", "url", "email", "tel"]);

function commitFocusedField(): void {
  const el = document.activeElement;
  const textLike =
    el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && COMMIT_ON_CHANGE.has(el.type));
  if (textLike) el.dispatchEvent(new Event("change", { bubbles: true }));
}

export function useTaskRoute(): void {
  const board = useBoard();
  const viewport = useViewport();

  /** The drawer only exists beside the tree and the chart, and as the tmux
   *  view's modal; a task opened from elsewhere (a notification, the widget)
   *  has to bring the tree and the chart back. */
  function showTasks(): void {
    if (hasDrawer(viewport.view)) return;
    if (viewport.view === "tmux" && board.tmuxAvailable) return;
    viewport.view = "board";
  }
  /** Set while the selection is being changed FROM the URL, so the watcher
   *  does not write straight back to history. */
  let fromHistory = false;
  /** Set while the drawer steps back over its own entry because it was closed
   *  or the view was switched: that is not the reader pressing back, and must
   *  not take them to the view the drawer was opened from. */
  let undoing = false;

  function selectFromUrl(): void {
    const ours = undoing;
    undoing = false;
    const url = new URL(location.href);
    const id = url.searchParams.get(PARAM);
    const next = id && board.tasks.has(id) ? id : null;
    if (next !== board.selectedId) commitFocusedField();
    fromHistory = true;
    if (next !== board.selectedId) board.select(next);
    fromHistory = false;
    if (next) showTasks();
    else if (!ours) {
      // Back from a drawer `openTaskFromView` opened: the entry names the view
      // it was opened from. Only a view with no drawer is followed — back from
      // a drawer opened on the board must not undo a switch to the timeline.
      const named = url.searchParams.get("view");
      if ((VIEWS as readonly (string | null)[]).includes(named) && !hasDrawer(named as View)) {
        viewport.view = named as View;
      }
    }
    // A link to a task that has since been deleted: drop it from the URL.
    if (id && !next) history.replaceState(history.state, "", urlFor(null));
    viewport.syncViewToUrl();
  }

  // Sync, so `fromHistory` is still set when the watcher runs.
  watch(
    () => board.selectedId,
    (next, prev) => {
      if (fromHistory) return;
      if (next && !prev) {
        history.pushState({ ...history.state, [PUSHED]: true }, "", urlFor(next));
      } else if (next) {
        history.replaceState(history.state, "", urlFor(next));
      } else if (history.state?.[PUSHED]) {
        // Undo our own push; popstate then finds no task and changes nothing.
        undoing = true;
        history.back();
      } else {
        history.replaceState(history.state, "", urlFor(null));
      }
    },
    { flush: "sync" },
  );

  // Every other view has no drawer, so leaving the tasks closes the one that
  // was open — and with it `?task=`, which would otherwise sit in the address
  // of the messages or the calendar and reopen the drawer on a reload.
  watch(
    () => viewport.view,
    (view) => {
      if (hasDrawer(view) || !board.selectedId) return;
      commitFocusedField();
      board.select(null);
    },
    { flush: "sync" },
  );

  // Signing in from the login form shows the shell before the board has
  // loaded; resolving the link then would find no task and drop it.
  const stop = watch(
    () => board.loaded,
    (loaded) => {
      if (!loaded) return;
      selectFromUrl();
      queueMicrotask(() => stop());
    },
    { immediate: true },
  );
  window.addEventListener("popstate", selectFromUrl);
  // Back from a deep link leaves the app outright, with no popstate first.
  window.addEventListener("pagehide", commitFocusedField);

  /** A tapped notification: its task, or the view it was about (public/sw.js). */
  function openNotice(msg: unknown): void {
    const { t, taskId, view, teamId } = (msg ?? {}) as { t?: string; taskId?: unknown; view?: unknown; teamId?: unknown };
    if (t !== "openNotice") return;
    // A team message opens that team's thread.
    if (typeof teamId === "string") board.switchTeam(teamId);
    if (typeof taskId === "string" && board.tasks.has(taskId)) {
      showTasks();
      board.select(taskId);
    } else if ((VIEWS as readonly unknown[]).includes(view)) {
      viewport.view = view as View;
    }
  }
  const onWorkerMessage = (ev: MessageEvent): void => openNotice(ev.data);
  navigator.serviceWorker?.addEventListener("message", onWorkerMessage);

  // The Android app's "+" on its home-screen widget: the same thing the board's
  // trailing "+ Add task" row does — a new task, opened to be named. A function
  // the app calls by script, never a URL: a `?new` link would let any page that
  // links here create tasks in whoever clicks it. A repo shortcut on the app's
  // icon (lib/appShortcuts.ts) passes its repo and the board it was on, as a
  // repo heading's own add row would.
  const loaded = (): Promise<void> =>
    board.loaded
      ? Promise.resolve()
      : new Promise((resolve) => {
          const stopWaiting = watch(() => board.loaded, (ok) => {
            if (!ok) return;
            queueMicrotask(() => stopWaiting());
            resolve();
          });
        });
  window[NATIVE] = {
    async newTask(into?: { repo?: unknown; team?: unknown }): Promise<void> {
      await loaded();
      const repo = typeof into?.repo === "string" && into.repo ? into.repo : null;
      if (repo && typeof into?.team === "string") board.switchTeam(into.team);
      const task = board.addTask({ repo });
      showTasks();
      board.select(task.id);
    },
    // A repo row on the widget: a quick task in that repo, as the repo
    // switcher's "+" makes one — queued for Claude, so only where the tmux view
    // is. On a board that does not list the repo, Personal, which lists them
    // all. Anyone without the tmux view gets a plain new task in the repo.
    async quickTask(repo: unknown): Promise<void> {
      await loaded();
      if (typeof repo !== "string" || !repo) return;
      if (!board.me?.isAdmin) {
        const task = board.addTask({ repo });
        showTasks();
        board.select(task.id);
        return;
      }
      const info = repoInfos.value.find((r) => r.name === repo);
      if (!board.tmuxAvailable || (info && !repoOnBoard(info, board.teamId, board.teams))) board.switchTeam(PERSONAL);
      quickTaskIn.value = repo;
      viewport.view = "tmux";
    },
    // A tapped notification about no task in particular: a team message.
    openView(view: string): void {
      openNotice({ t: "openNotice", view });
    },
    // A board heading tapped on an older app's widget (it lists repos now): that team's tasks, or Personal's.
    async openTeam(id: string): Promise<void> {
      await loaded();
      board.switchTeam(id);
      showTasks();
    },
  };

  onBeforeUnmount(() => {
    window.removeEventListener("popstate", selectFromUrl);
    window.removeEventListener("pagehide", commitFocusedField);
    navigator.serviceWorker?.removeEventListener("message", onWorkerMessage);
    delete window[NATIVE];
  });
}

/**
 * What the Android app may call on the page (android/…/MainActivity.kt). It is
 * only there while someone is signed in, which is when there is a board to act
 * on; the app waits for it rather than assuming.
 */
const NATIVE = "__todosNative";

declare global {
  interface Window {
    [NATIVE]?: {
      newTask(into?: { repo?: unknown; team?: unknown }): Promise<void>;
      quickTask(repo: unknown): Promise<void>;
      openView(view: string): void;
      openTeam(id: string): Promise<void>;
    };
  }
}
